import { forwardRef, useEffect, useMemo, useState } from 'react';
import { Divider, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormik, yupToFormErrors } from 'formik';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import Button from 'components/new-ui/base/button/Button';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import { H3 } from 'components/new-ui/base/typography/styles';
import { ICS2CodelistsMap } from 'store/codelists/code';
import useIcs2CodelistLabel from 'hooks/useIcs2CodelistLabel';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { getIcs2ReferralAmendments } from 'store/declarations/eu/ics2/customs-requests/client';
import { Ics2AmendmentStatus, Ics2AmendmentSummary } from 'store/declarations/eu/ics2/customs-requests/types';
import {
    ICS2_ATTACHMENT_FILE_TYPES,
    ICS2_MAX_ATTACHMENTS_PER_REFERRAL,
    isSelectableIcs2Document,
} from 'store/file-upload/ics2';
import AttachmentsField from '../../AttachmentsField';
import { ResponseFormHandle } from '../../responseForm';
import useResponseFormHandle from '../../useResponseFormHandle';
import { describeDocumentRequest } from '../evidenceExpectation';
import { ResponseFormProps } from '../responseForm';
import { validationSchema } from './validationSchema';
import {
    CODE_REQUIRING_AMENDMENT,
    FormFields,
    FormValues,
    MAX_INFORMATION_ENTRIES,
    emptyInformationEntry,
    fromReferralDraft,
    toReferralDraft,
} from './types';

type Props = ResponseFormProps<ReturnType<typeof toReferralDraft>>;

/**
 * The IE3R02 answer to one RFI or AMD referral.
 *
 * There is no information-type control: CL703 allows only `R2` on a response and the backend sets
 * it. The response code list is rendered straight from CL752 rather than hardcoded, because the
 * backend validates membership against the same CMS list.
 */
const ResponseForm = forwardRef<ResponseFormHandle, Props>(
    ({ declarationId, notificationId, referral, attachments, disabled = false, onSubmit, onSave }, ref) => {
        const [amendments, setAmendments] = useState<Ics2AmendmentSummary[]>([]);
        const getLabel = useIcs2CodelistLabel([ICS2CodelistsMap.CL013]);
        const attachmentCount = attachments.selectableCount;

        const initialValues = useMemo<FormValues>(() => fromReferralDraft(referral), [referral]);

        const formik = useFormik<FormValues>({
            initialValues,
            enableReinitialize: true,
            validate: (values) => {
                try {
                    validationSchema.validateSync(values, { abortEarly: false, context: { attachmentCount } });
                    return {};
                } catch (error) {
                    return yupToFormErrors(error);
                }
            },
            onSubmit: async (values) => {
                await onSubmit?.(toReferralDraft(values));
            },
        });

        useResponseFormHandle(ref, {
            formik,
            toDraft: toReferralDraft,
            onSave,
            attachmentErrors: attachments.attachmentErrors,
        });

        const requestedDocuments = describeDocumentRequest(referral, (type) => getLabel(type, ICS2CodelistsMap.CL013));

        const usesAmendmentCode = formik.values.additionalInformation.some(
            (entry) => entry.code === CODE_REQUIRING_AMENDMENT
        );

        const entries = formik.values.additionalInformation;

        const addEntry = () =>
            formik.setFieldValue(FormFields.ADDITIONAL_INFORMATION, [...entries, { ...emptyInformationEntry }]);

        const removeEntry = (index: number) =>
            formik.setFieldValue(
                FormFields.ADDITIONAL_INFORMATION,
                entries.filter((_, position) => position !== index)
            );

        useEffect(() => {
            if (!usesAmendmentCode || !declarationId || !notificationId) return;
            getIcs2ReferralAmendments(declarationId, notificationId)
                .then((summaries) =>
                    setAmendments(summaries.filter((summary) => summary.status === Ics2AmendmentStatus.ACCEPTED))
                )
                .catch(() => setAmendments([]));
        }, [usesAmendmentCode, declarationId, notificationId]);

        return (
            <form onSubmit={formik.handleSubmit}>
                <section>
                    <Space size={16} direction="vertical" style={{ width: '100%' }}>
                        <H3>Response Details</H3>

                        {entries.map((_, index) => {
                            const codePath = `${FormFields.ADDITIONAL_INFORMATION}[${index}].code`;
                            const textPath = `${FormFields.ADDITIONAL_INFORMATION}[${index}].text`;

                            return (
                                <Space
                                    key={index}
                                    size={6}
                                    direction="vertical"
                                    style={{ width: '100%', paddingBottom: entries.length > 1 ? 8 : 0 }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <label htmlFor={`select-id-${codePath}`}>
                                            <LabelRequiredSymbol /> Response Code
                                        </label>
                                        {entries.length > 1 && !disabled && (
                                            <DeleteOutlined
                                                style={{ color: 'var(--dust-red-5)', cursor: 'pointer' }}
                                                onClick={() => removeEntry(index)}
                                            />
                                        )}
                                    </div>
                                    <DeclarationSelect
                                        fieldProps={formik.getFieldProps(codePath)}
                                        fieldHelper={formik.getFieldHelpers(codePath)}
                                        fieldMeta={formik.getFieldMeta(codePath)}
                                        condensed
                                        disabled={disabled}
                                        hideCodelistMenu
                                        codeValidation={[]}
                                        placeholder="Select Response Code"
                                        codelistProps={{
                                            codelistName: ICS2CodelistsMap.CL752,
                                            declarationType: DeclarationTypes.ICS2,
                                        }}
                                    />

                                    <label htmlFor={textPath}>Response Text</label>
                                    <FormTextArea
                                        id={textPath}
                                        placeholder="Max 512 chars"
                                        disabled={disabled}
                                        fieldProps={formik.getFieldProps(textPath)}
                                        fieldHelper={formik.getFieldHelpers(textPath)}
                                        fieldMeta={formik.getFieldMeta(textPath)}
                                    />
                                </Space>
                            );
                        })}

                        {entries.length < MAX_INFORMATION_ENTRIES && !disabled && (
                            <Button icon={<PlusOutlined />} onClick={addEntry}>
                                Add another response
                            </Button>
                        )}
                    </Space>
                </section>

                {usesAmendmentCode && (
                    <>
                        <Divider />
                        <section>
                            <Space size={6} direction="vertical" style={{ width: '100%' }}>
                                <label htmlFor={FormFields.ACCEPTED_AMENDMENT_REQUEST_ID}>
                                    <LabelRequiredSymbol /> Amendment
                                </label>
                                <Typography.Text style={{ fontSize: 12 }}>
                                    Code {CODE_REQUIRING_AMENDMENT} must point at an amendment customs has already
                                    accepted.
                                </Typography.Text>
                                <Select
                                    id={FormFields.ACCEPTED_AMENDMENT_REQUEST_ID}
                                    style={{ width: '100%' }}
                                    placeholder={
                                        amendments.length
                                            ? 'Select an accepted amendment'
                                            : 'No accepted amendment is available yet'
                                    }
                                    disabled={disabled || !amendments.length}
                                    value={formik.values.acceptedAmendmentRequestId}
                                    onChange={(value) =>
                                        formik.setFieldValue(FormFields.ACCEPTED_AMENDMENT_REQUEST_ID, value)
                                    }
                                    options={amendments.map((amendment) => ({
                                        value: amendment.requestId,
                                        label: amendment.transactionId ?? amendment.requestId,
                                    }))}
                                />
                            </Space>
                        </section>
                    </>
                )}

                <Divider />

                <AttachmentsField
                    acceptedTypes={ICS2_ATTACHMENT_FILE_TYPES}
                    maxCount={ICS2_MAX_ATTACHMENTS_PER_REFERRAL}
                    hint="Attach supporting documents such as invoices, packing lists or certificates."
                    requested={requestedDocuments}
                    disabled={disabled}
                    documents={attachments.documents}
                    isLoading={attachments.isLoading}
                    isUploading={attachments.isUploading}
                    canAddMore={attachments.canAddMore}
                    isRemovable={isSelectableIcs2Document}
                    onUpload={attachments.upload}
                    onRemove={attachments.remove}
                    onDownload={attachments.download}
                    onPreview={attachments.preview}
                />
            </form>
        );
    }
);

export default ResponseForm;
